import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { endpoints } from '../../utils/endpoints';
import {
    DistributedCustomReward,
    DistributePlayersRequest,
    RewardSetting,
    SaveTournamentBroadcastingServicesRequest,
    TournamentSupervizersInput,
    TournamentCustomRewardsDistributionRequest,
    TournamentQualifiersLeaderboardParticipant,
    TournamentRewardsSettingsRequest,
    TournamentSupervizersSaveInput,
    TournamentStageSaveInput,
    TournamentStageLevelSaveInput,
    TournamentRoundSaveInput,
} from '../models/api/requests/tournamentGeneral.request';
import {
    GetBroadcastingServiceResponse,
    TournamentResult,
    UpdateTournamentStateResponse,
} from '../models/api/responses/tournamentGeneral.response';
import {
    Tournament,
    TournamentScoringSetting,
} from '../models/tournaments.interface';

@Injectable({
    providedIn: 'root',
})
export class TournamentGeneralService {
    private http = inject(HttpClient);


    getTournamentById(id: string): Observable<Tournament> {
        return this.http
            .get<Tournament>(
                endpoints.tournaments.children.getTournamentById(id)
            )
            .pipe(shareReplay());
    }

     getTournamentByIdV2(id: string): Observable<Tournament> {
        return this.http
            .get<Tournament>(
                endpoints.tournaments.children.getTournamentByIdV2(id)
            )
            .pipe(shareReplay());
    }

    updateTournamentState({
        id,
        state,
        useV2Base,
    }: {
        id: string;
        state: string;
        useV2Base?: boolean;
    }): Observable<UpdateTournamentStateResponse> {
        return this.http.post<UpdateTournamentStateResponse>(
            endpoints.tournaments.children.updateTournamentState({
                id,
                state,
                useV2Base,
            }),
            {}
        );
    }

    distributePlayers(
        tournamentId: string,
        request: DistributePlayersRequest[]
    ) {
        return this.http.post<any>(
            endpoints.tournaments.children.getDistributePlayersUrl(
                tournamentId
            ),
            request
        );
    }

    getTournamentScoringSettingsByTournamentId(tournamentId: string) {
        return this.http
            .get<TournamentScoringSetting[]>(
                endpoints.tournaments.children.getTournamentScoringSettingsByIdUrl(
                    tournamentId
                )
            )
            .pipe(shareReplay());
    }

    updateTournamentScoringSettings(
        tournamentId: string,
        scoringSettings: TournamentScoringSetting[]
    ) {
        return this.http.post<TournamentScoringSetting[]>(
            endpoints.tournaments.children.getTournamentScoringSettingsByIdUrl(
                tournamentId
            ),
            {
                scoringSettings,
            }
        );
    }

    getBroadcastingServices() {
        return this.http.get<GetBroadcastingServiceResponse[]>(
            endpoints.tournaments.children.getBroadcastingServices()
        );
    }

    fetchTournamentResults(tournamentId: string) {
        return this.http.get<TournamentResult[]>(
            endpoints.tournaments.children.getTournamentResults(tournamentId)
        );
    }

    fetchTournamentResultsV2(tournamentId: string) {
        return this.http.get<TournamentResult[]>(
            endpoints.tournaments.children.getTournamentResultsV2(tournamentId)
        );
    }

    addTournamentBroadcastingServices(
        tournamentId: string,
        request: SaveTournamentBroadcastingServicesRequest
    ) {
        return this.http.post<Tournament>(
            endpoints.tournaments.children.saveTournamentBroadcastingServices(
                tournamentId
            ),
            request
        );
    }

    updateTournamentBroadcastingServices(
        tournamentId: string,
        request: SaveTournamentBroadcastingServicesRequest
    ) {
        return this.http.put<Tournament>(
            endpoints.tournaments.children.saveTournamentBroadcastingServices(
                tournamentId
            ),
            request
        );
    }

    fetchTournamentRewardsSettings(tournamentId: string, useV2Base?: boolean) {
        return this.http.get<RewardSetting[]>(
            endpoints.tournaments.children.getRewardsSettingsUrl(tournamentId, useV2Base)
        );
    }

    saveTournamentRewardsSettings(
        tournamentId: string,
        request: TournamentRewardsSettingsRequest,
        useV2Base?: boolean
    ) {
        return this.http.post<RewardSetting[]>(
            endpoints.tournaments.children.getRewardsSettingsUrl(tournamentId, useV2Base),
            request
        );
    }

    fetchDistributedCustomRewards(tournamentId: string) {
        return this.http.get<DistributedCustomReward[]>(
            endpoints.tournaments.children.getCustomRewardDistributionUrl(
                tournamentId
            )
        );
    }

    saveDistributedCustomRewards(
        tournamentId: string,
        request: TournamentCustomRewardsDistributionRequest
    ) {
        return this.http.post<DistributedCustomReward[]>(
            endpoints.tournaments.children.getCustomRewardDistributionUrl(
                tournamentId
            ),
            request
        );
    }

    fetchTournamentQualifiersLeaderboard(tournamentId: string) {
        return this.http.get<TournamentQualifiersLeaderboardParticipant[]>(
            endpoints.tournaments.children.getTournamentQualifiersLeaderboard(tournamentId)
        );
    }

    saveTournamentSupervizers(
        tournamentId: string,
        request: TournamentSupervizersSaveInput
    ) {
        return this.http.post<TournamentSupervizersInput>(
            endpoints.tournaments.children.addSupervisers(
                tournamentId
            ),
            request
        );
    }

    getTournamentProject(id: string) {
        return this.http.get<any>(endpoints.builder.children.getProjectDataById(id)).pipe(shareReplay());
    }

    saveTournamentProjectStage(
        request: TournamentStageSaveInput
    ) {
        return this.http.post<TournamentStageSaveInput>(
            endpoints.builder.children.saveStage(),
            request
        );
    }

    saveTournamentProjectStageLevel(
        request: TournamentStageLevelSaveInput
    ) {
        return this.http.post<TournamentStageLevelSaveInput>(
            endpoints.builder.children.saveStageLevel(),
            request
        );
    }

    deleteTournamentProjectStage(
        id: string
    ) {
        return this.http.delete<void>(
            endpoints.builder.children.deleteStage(id),
        );
    }

    deleteTournamentProjectStageLevel(
        id: string
    ) {
        return this.http.delete<void>(
            endpoints.builder.children.deleteStageLevel(id)
        );
    }

    saveTournamentProjectRound(
        stageId: string,
        input: TournamentRoundSaveInput[],
        schema: string,
    ) {
        var request = {
            scheme: schema,
            data: JSON.stringify(input)
        }
        return this.http.put<void>(
            endpoints.builder.children.saveRound(stageId),
            request
        );
    }

    finalizeTournamentProject(
        tournamentProjectId: string,
    ) {
        return this.http.post<void>(
            endpoints.builder.children.finalizeProject(tournamentProjectId), {}
        );
    }
}
